import React, { FunctionComponent, useContext, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from '@wix/wix-i18n-config';
import { Box, Heading, TextButton } from '@wix/design-system';
import ArrowLeftIcon from 'wix-ui-icons-common/ArrowLeft';
import ArrowRightIcon from 'wix-ui-icons-common/ArrowRight';
import { Category } from '@wix/answers-api';
import { pageClick } from '@wix/bi-logger-new-help-center/v2';
import { isMobile } from '../../utils/userAgent';
import { CategoryListMobile } from './CategoryListMobile';
import { DATA_HOOKS } from '../../dataHooks';
import { useWindowSize } from '../../hooks/useWindowSize';
import { BI } from '../../constants';
import { useBI } from '../../hooks/useBI';
import { Context } from '../../context';
import { pushUrl } from '../InnerLink';
import { isRTL } from '../../utils/internationalization';
import css from './index.module.scss';

export type EventCategoryData = {
  clickedUrl?: string;
  clickedText?: string;
  clickedItemOrder?: string;
  clickedItemId?: string;
  categoryOrder?: number;
};

type CategoryListProps = {
  category: Category;
  subCategory?: Category;
  categories: Category[];
  changeSubCategory: (subCategory: Category) => void;
  changeParentCategory: (category: Category) => void;
};

export const CategoryList: FunctionComponent<CategoryListProps> = ({
  category,
  subCategory,
  categories,
  changeSubCategory,
  changeParentCategory,
}) => {
  const { locale } = useRouter();
  const { sendBIEvent } = useBI();
  const context = useContext(Context);
  const [isAllTopics, setIsAllTopics] = useState<boolean>(false);
  useWindowSize();
  const { t } = useTranslation();
  const isRTLLanguage = isRTL(locale);

  const isFocus = (child: Category): boolean => {
    if (isAllTopics) {
      return child.id === category.id;
    } else {
      return child.id === subCategory?.id;
    }
  };

  const pageClickEvent = async (
    clickedItemType: string,
    eventData: EventCategoryData
  ) => {
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.CATEGORY,
        kb_lang: locale as string,
        item_id: subCategory?.id,
        clicked_item_type: clickedItemType,
        clicked_url: eventData.clickedUrl,
        clicked_text: eventData.clickedText,
        clicked_item_order: eventData.clickedItemOrder,
        clicked_item_id: eventData.clickedItemId,
      })
    );
  };

  const backButtonClick = async () => {
    if (isAllTopics) {
      await pageClickEvent(BI.CLICKED_ITEM_TYPES.ALL_TOPICS, {
        clickedUrl: `/${locale}`,
        clickedText: t('help.center'),
      });
      await pushUrl(`/${locale}`, context.isProductionDomain);
    } else {
      setIsAllTopics(true);
      await pageClickEvent(BI.CLICKED_ITEM_TYPES.ALL_TOPICS, {
        clickedText: t('category.all.topics'),
      });
    }
  };

  const categoryClick = async (item: Category, index: string) => {
    let clickedItemType;
    if (isAllTopics) {
      clickedItemType = BI.CLICKED_ITEM_TYPES.CATEGORY_SELECTION;
      changeParentCategory(item);
      setIsAllTopics(false);
    } else {
      clickedItemType = BI.CLICKED_ITEM_TYPES.SUB_CATEGORY_SELECTION;
      changeSubCategory(item);
    }
    await pageClickEvent(clickedItemType, {
      clickedItemId: item.id,
      clickedItemOrder: index,
      clickedText: item.name,
      clickedUrl: item.url,
    });
  };

  if (isMobile(context)) {
    return (
      <CategoryListMobile
        categories={categories}
        category={category}
        subCategory={subCategory}
        changeSubCategory={changeSubCategory}
        changeParentCategory={changeParentCategory}
        pageClickEvent={pageClickEvent}
      />
    );
  }

  return (
    <Box
      key={isAllTopics ? 'isAllTopicsMode' : category.id}
      direction="vertical"
      className={`${css.categorySection} ${css.slideIn}`}
    >
      <Box direction="vertical" className={css.categoriesTitleList}>
        <TextButton
          size="small"
          onClick={backButtonClick}
          prefixIcon={isRTLLanguage ? <ArrowRightIcon /> : <ArrowLeftIcon />}
          dataHook={DATA_HOOKS.CATEGORY_BACK_BTN}
          className={css.backButton}
        >
          {isAllTopics ? t('help.center') : t('category.all.topics')}
        </TextButton>

        {category && (
          <Heading
            size="medium"
            as="div"
            className={css.allTopicsOrCategoryName}
          >
            {isAllTopics
              ? t('category.all.topics.count', { count: categories?.length })
              : category.name}
          </Heading>
        )}
      </Box>
      <Box direction="vertical" className={css.categoryList}>
        {(isAllTopics ? categories : category?.children)?.map((item, index) => (
          <TextButton
            key={item.id}
            className={`${css.categoryItem} ${isFocus(item) ? css.focus : ''}`}
            onClick={() => categoryClick(item, `${index + 1}`)}
            dataHook={DATA_HOOKS.CATEGORY_ITEM}
          >
            {item.name}
          </TextButton>
        ))}
      </Box>
    </Box>
  );
};
